import { supabase } from "@/integrations/supabase/client";
import { createNotifications, type CreateNotificationInput } from "@/api/notifications";

/**
 * Request creation notifications (UAT 3.5b).
 *
 * Follows the same client-side pattern as task-notifications.ts and
 * mention-notifications.ts: fan out rows into `notifications` via the
 * shared API helpers right after the action succeeds. There is no DB
 * trigger or automation event for request creation — the only
 * server-side mechanism (process-request-escalations) is a cron job
 * that fires *hours later* for still-unclaimed care requests with a
 * distinct "request_escalation" notification, so notifying at creation
 * time here does not double up with it.
 *
 * Routing:
 * - Request created with a triage owner → notify that triage owner.
 * - Request created without a triage owner ("needs attention") →
 *   notify the org's admins so somebody picks it up.
 * The creator is never notified about their own request.
 */

export interface NotifyRequestCreatedOptions {
  organizationId: string;
  requestId: string;
  summary: string;
  /** UI category (already normalized) — stored in metadata for context. */
  category: string;
  /** Person id of the assigned triage owner, when one was chosen. */
  triageOwnerPersonId: string | null;
  /** auth user id of the creator — used to resolve the actor + skip self-notification. */
  creatorUserId: string | null;
}

/**
 * Emit in-app notifications for a newly created request.
 *
 * Fire-and-forget: never throws — notification fan-out must not break
 * request creation (mirrors createTaskAssignmentNotification).
 */
export async function notifyRequestCreated(options: NotifyRequestCreatedOptions): Promise<void> {
  const {
    organizationId,
    requestId,
    summary,
    category,
    triageOwnerPersonId,
    creatorUserId,
  } = options;

  try {
    let recipientPersonIds: string[] = [];
    let actorPersonId: string | null = null;
    let title: string;

    if (triageOwnerPersonId) {
      // Assigned at creation → tell the triage owner.
      title = "New request assigned to you for triage";
      recipientPersonIds = [triageOwnerPersonId];

      if (creatorUserId) {
        const { data: actor } = await supabase
          .from("people")
          .select("id")
          .eq("organization_id", organizationId)
          .eq("user_id", creatorUserId)
          .maybeSingle();
        actorPersonId = actor?.id ?? null;
      }
    } else {
      // Needs attention (no triage owner) → tell the org's admins.
      title = "New request needs triage";

      const { data: recipientRows, error: recipientsError } = await supabase.rpc(
        "get_request_triage_notification_recipients",
        {
          p_organization_id: organizationId,
          p_creator_user_id: creatorUserId,
        }
      );

      if (recipientsError) {
        console.error("Failed to resolve triage recipients for request notification:", recipientsError);
        return;
      }

      const rows = recipientRows ?? [];
      actorPersonId = rows.find((row) => row.is_actor)?.person_id ?? null;
      recipientPersonIds = [
        ...new Set(
          rows
            .filter((row) => row.is_recipient)
            .map((row) => row.person_id)
        ),
      ];
    }

    // Never notify the creator about their own request.
    const targets = recipientPersonIds.filter((personId) => personId !== actorPersonId);
    if (targets.length === 0) return;

    const notifications: CreateNotificationInput[] = targets.map((personId) => ({
      organizationId,
      personId,
      actorPersonId,
      title,
      body: summary,
      // Deep-link route — RequestRedirect derives the lane and opens the drawer.
      linkUrl: `/requests/${requestId}`,
      linkLabel: "View request",
      notificationType: "assignment",
      metadata: {
        kind: "request_created",
        request_id: requestId,
        category,
      },
    }));

    await createNotifications(notifications);
  } catch (error) {
    console.error("Failed to create request notifications:", error);
    // Don't throw — notification fan-out is not critical.
  }
}
